import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Quote, Play, MapPin, Phone, Mail, Clock } from "lucide-react";
import { FIRM } from "../data/content";
import { FALLBACK_POSTS, FALLBACK_VIDEOS, CHANNEL_UPLOADS_EMBED } from "../data/fallbackContent";
import { useConsultation } from "../components/ConsultationContext";
import Reveal from "../components/Reveal";
import SafeImage from "../components/SafeImage";

const FALLBACK_IMG = "https://images.pexels.com/photos/6927546/pexels-photo-6927546.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
}

export default function Home() {
  const consult = useConsultation();
  // Initialise with curated fallback so the page never renders empty/loading sections.
  // The arrays are replaced in-place when the live feeds resolve.
  const [posts, setPosts] = React.useState(FALLBACK_POSTS);
  const [videos, setVideos] = React.useState(FALLBACK_VIDEOS);

  React.useEffect(() => {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 7000);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blogger/posts?limit=6`, { signal: ctrl.signal })
      .then((r) => r.json())
      .then((d) => {
        if (Array.isArray(d.items) && d.items.length) setPosts(d.items);
      })
      .catch(() => { /* keep fallback content */ })
      .finally(() => clearTimeout(t));

    const ctrlV = new AbortController();
    const tV = setTimeout(() => ctrlV.abort(), 7000);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/youtube/videos?limit=4`, { signal: ctrlV.signal })
      .then((r) => r.json())
      .then((d) => {
        if (Array.isArray(d.items) && d.items.length) setVideos(d.items);
      })
      .catch(() => { /* keep fallback content */ })
      .finally(() => clearTimeout(tV));
  }, []);

  const featured = posts[0];
  const latestPosts = posts.slice(1, 4);
  const featuredVideo = videos[0];
  const latestVideos = videos.slice(1, 4);

  return (
    <div data-testid="home-page">
      {/* ================= 1. HERO ================= */}
      <section data-testid="hero-section" className="relative overflow-hidden bg-[#0A192F] text-white min-h-screen flex flex-col">
        <div className="absolute inset-0 grain pointer-events-none" aria-hidden />
        <div className="absolute -top-[40rem] -right-[40rem] h-[64rem] w-[64rem] rounded-full bg-[#C5A059]/6 blur-[160px]" aria-hidden />

        <div className="relative container-x flex-1 flex flex-col justify-center pt-48 lg:pt-60 pb-32">
          <div className="reveal max-w-[1400px]">
            <p className="eyebrow text-[#C5A059]">Chartered Accountants · Established 1998</p>
            <h1 className="editorial-display text-white text-[clamp(3rem,10.4vw,11.5rem)] mt-14 leading-[0.96]">
              Providing<br />
              <span className="italic text-[#C5A059] font-light">Tax, Audit &amp; Advisory</span><br />
              <span className="text-white/85">Services Since 1998.</span>
            </h1>
          </div>
        </div>

        <div className="relative container-x pb-24 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-end">
            <Reveal className="lg:col-span-6" delay={200}>
              <p className="text-white/55 text-lg lg:text-xl leading-[1.7] max-w-[560px]">
                A Chartered Accountancy firm offering services in taxation, audit, compliance and advisory to businesses, professionals and individuals.
              </p>
              <div className="mt-14 flex flex-wrap gap-4">
                <Link to="/contact" data-testid="hero-primary-cta" className="btn-gold">
                  Get in Touch <ArrowRight size={16} />
                </Link>
                <Link to="/services" data-testid="hero-secondary-cta" className="btn-ghost-light">
                  Explore Services <ArrowUpRight size={16} />
                </Link>
              </div>
            </Reveal>
            <Reveal className="lg:col-span-4 lg:col-start-9" delay={300}>
              <div className="border-t border-white/12 pt-7">
                <p className="overline text-white/35 text-[10px]">Practice · Credentials</p>
                <p className="font-serif text-2xl mt-5 leading-snug">28+ Years <span className="text-white/30 px-1">·</span> FCA · ACMA · DISA (ICAI)</p>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="relative border-t border-white/8 py-7 overflow-hidden">
          <div className="marquee-track text-white/75 text-[11px] tracking-[0.42em] uppercase font-normal">
            {Array.from({ length: 2 }).map((_, k) => (
              <span key={k} className="inline-flex gap-20">
                <span>Tax Consultancy and Advisory</span><span className="text-[#C5A059]/90">·</span>
                <span>Tax Compliance and Return Filing</span><span className="text-[#C5A059]/90">·</span>
                <span>Audit and Assurance Services</span><span className="text-[#C5A059]/90">·</span>
                <span>Regulatory and Compliance Services</span><span className="text-[#C5A059]/90">·</span>
                <span>NRI Advisory</span><span className="text-[#C5A059]/90">·</span>
                <span>MSME Compliance</span><span className="text-[#C5A059]/90">·</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ================= 3. SERVICES ================= */}
      <section data-testid="services-section" className="bg-white">
        <div className="container-x py-44 lg:py-64">
          <Reveal>
            <p className="eyebrow text-[#C5A059]">Our Professional Services</p>
            <h2 className="editorial-display text-[#0A192F] text-[clamp(2.6rem,7vw,7rem)] mt-14 leading-[0.96]">
              Taxation, audit, compliance <br /><span className="italic text-[#C5A059]">and advisory.</span>
            </h2>
            <p className="mt-12 text-[#475569] text-lg lg:text-xl leading-[1.85] max-w-[60ch]">
              The Firm provides a comprehensive range of professional services in the areas of taxation, audit, compliance and advisory.
            </p>
          </Reveal>

          <Reveal delay={150}>
            <div className="mt-20 lg:mt-24">
              <Link to="/services" data-testid="services-explore-link" className="btn-ghost-dark">
                Explore Services <ArrowUpRight size={14} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= 4. INDUSTRIES ================= */}


      {/* ================= 5. THOUGHT LEADERSHIP ================= */}
      <section data-testid="leadership-section" className="bg-white">
        <div className="container-x py-44 lg:py-64">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="eyebrow text-[#C5A059]">Thought Leadership</p>
                <h2 className="editorial-display text-[#0A192F] text-[clamp(2.4rem,5.6vw,5.6rem)] mt-12 leading-[0.96]">
                  Knowledge, <br /><span className="italic text-[#C5A059]">shared in the open.</span>
                </h2>
              </Reveal>
              <Reveal delay={150}>
                <p className="text-[#475569] mt-12 text-lg lg:text-xl leading-[1.85] max-w-[40ch]">
                  We share practical insights and updates on taxation and compliance through our YouTube channel.
                </p>
                <div className="mt-12 flex flex-wrap gap-4">
                  <Link to="/media" data-testid="leadership-media-link" className="btn-ghost-dark">View Videos <ArrowUpRight size={14} /></Link>
                </div>
              </Reveal>
            </div>
            <Reveal className="lg:col-span-7" delay={200}>
              <div className="relative aspect-video w-full bg-[#0A192F]">
                <iframe
                  data-testid="youtube-embed"
                  className="absolute inset-0 w-full h-full"
                  src={featuredVideo ? featuredVideo.embed_url : `https://www.youtube.com/embed/${FIRM.contact.youtubeEmbedId}`}
                  title="CA. NRK"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <p className="overline text-[#64748B] text-[10px] mt-7 flex items-center justify-between">
                <span>Featured · CA. NRK</span>
                <span className="font-sans normal-case tracking-normal text-[12px] text-[#475569]">Live from YouTube</span>
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ================= 6. FEATURED INSIGHT ================= */}
      {featured && (
        <section data-testid="featured-insight-section" className="bg-[#FBFAF6]">
          <div className="container-x py-44 lg:py-64">
            <Reveal>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                <div className="lg:col-span-8">
                  <p className="eyebrow text-[#C5A059]">Featured Insight</p>
                  <p className="mt-10 text-[#475569] text-lg lg:text-xl leading-[1.85] max-w-[60ch]">
                    Updates on taxation, audit and compliance are shared through our insights and articles.
                  </p>
                </div>
                <div className="lg:col-span-4 lg:text-right">
                  <Link to="/insights" data-testid="insights-all-link" className="btn-ghost-dark">View all insights <ArrowUpRight size={14} /></Link>
                </div>
              </div>
            </Reveal>
            <Reveal delay={150}>
              <Link to={`/insights/${featured.slug}`} data-testid={`featured-insight-${featured.slug}`} className="group block mt-16 lg:mt-24">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                  <div className="lg:col-span-7 order-2 lg:order-1">
                    <p className="overline text-[#C5A059] text-[10px]">{featured.category} · {featured.read_time} · {fmtDate(featured.published)}</p>
                    <h3 className="editorial-display text-[#0A192F] text-[clamp(2.2rem,5.4vw,5rem)] mt-7 leading-[1.0] group-hover:text-[#C5A059] transition-colors duration-700">
                      {featured.title}
                    </h3>
                    <p className="text-[#475569] mt-9 text-lg leading-[1.85] max-w-[60ch]">{featured.excerpt}</p>
                    <span className="overline text-[#0A192F] mt-12 inline-flex items-center gap-2 text-[10px] group-hover:text-[#C5A059] transition-colors">Read full article <ArrowUpRight size={14} /></span>
                  </div>
                  <div className="lg:col-span-5 order-1 lg:order-2">
                    <div className="aspect-[4/5] overflow-hidden bg-white">
                      <img src={featured.image || FALLBACK_IMG} alt={featured.title} className="w-full h-full object-cover grayscale-[8%] group-hover:scale-105 transition duration-[1400ms]" />
                    </div>
                  </div>
                </div>
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      {/* ================= 11. OFFICE CONTACT + MAP ================= */}
      <section data-testid="office-section" className="bg-[#FBFAF6]">
        <div className="container-x py-32 lg:py-44">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <Reveal className="lg:col-span-5">
              <p className="eyebrow text-[#C5A059]">Our Office</p>
              <h2 className="editorial-display text-[#0A192F] text-[clamp(2.2rem,4.6vw,4.4rem)] mt-10 leading-[1.04]">
                Visit our office or <span className="italic text-[#C5A059]">reach out.</span>
              </h2>
              <ul className="mt-12 space-y-7 text-[#0A192F]">
                <li className="flex items-start gap-4"><MapPin size={18} className="text-[#C5A059] mt-1 flex-shrink-0" /> <span>{FIRM.contact.address1}<br /><span className="text-[#475569]">{FIRM.contact.address2}</span></span></li>
                <li className="flex items-center gap-4"><Phone size={18} className="text-[#C5A059]" /> <a className="link-underline" href={`tel:${FIRM.contact.phone}`}>{FIRM.contact.phone}</a></li>
                <li className="flex items-center gap-4"><Mail size={18} className="text-[#C5A059]" /> <a className="link-underline" href={`mailto:${FIRM.contact.email}`}>{FIRM.contact.email}</a></li>
                <li className="flex items-center gap-4"><Clock size={18} className="text-[#C5A059]" /> <span>Monday to Saturday · 10:00 AM – 5:00 PM IST</span></li>
              </ul>
              <Link to="/contact" data-testid="office-contact-link" className="btn-ghost-dark mt-12">Send a message <ArrowUpRight size={14} /></Link>
            </Reveal>

            <Reveal className="lg:col-span-7" delay={150}>
              <div className="aspect-[5/4] overflow-hidden bg-white border border-[#0A192F]/10">
                <iframe
                  data-testid="office-map"
                  src={FIRM.contact.mapEmbed}
                  title="Office location"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
